import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { TdDialogService } from '@covalent/core/dialogs';
import { combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { USER } from 'src/app/core/user.service';
import { LogMode } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { FlightLogHttpClient } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.service';
import { FlightSummaryDto } from 'src/app/shared/flight-log-api/flights/flight-client.model';
import { FlightSummaryHttpClient } from 'src/app/shared/flight-log-api/flights/flight-client.service';
import { formatMomentDate } from 'src/util/dateLib';
import { firstValueOrNull } from 'src/util/rxjs/observables/first-value';
import { humanize } from 'src/util/text';
import { uuid } from 'src/util/uuid';
import { UserViewModel } from 'src/view-models/user-view-model';

@Component({
  selector: 'fl-find-flight-dialog',
  templateUrl: './find-flight-dialog.component.html',
  styleUrls: ['./find-flight-dialog.component.scss'],
})
export class FindFlightDialogComponent implements OnInit {
  public form: UntypedFormGroup;
  public searchResults: { flightSummary: FlightSummaryDto; data: any[] } | null;
  public submitting: boolean;
  public flightNotFound: boolean;

  // The user must be dutied on to one of these bases before they'll be seen as disconnected.

  public disconnectedBases: { id: string; name: string }[] = [
    {
      id: 'd1d8a0ec0746102ff84baead9e2319f3',
      name: 'RCH 012 - San Antonio, TX',
    },
  ];

  public dataColumns: { name: string; label: string }[] = [
    { name: 'dateOfService', label: 'Date of Service' },
    { name: 'aircraftTailNumber', label: 'Tail Number' },
    { name: 'status', label: 'Log Status' },
  ];
  public columns = this.dataColumns.map((c) => c.name).concat('actions');

  get dispatchId(): AbstractControl {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.form.get('dispatchId')!;
  }

  get hasFlightLogData(): boolean {
    return (
      this.searchResults != null &&
      this.searchResults.data != null &&
      this.searchResults.data.length > 0
    );
  }

  constructor(
    private _fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<FindFlightDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject(USER) private _user: UserViewModel,
    private _dialogService: TdDialogService,
    private _flights: FlightSummaryHttpClient,
    private _flightLogs: FlightLogHttpClient,
    private _router: Router,
  ) {}

  public ngOnInit() {
    this.setDefaultDialogSize();

    this.form = this._fb.group({
      dispatchId: ['', Validators.required],
    });
  }

  private async isUserBaseDisconnected(): Promise<boolean> {
    const latestShift = await this._user.latestShift$.pipe(take(1)).toPromise();
    if (latestShift != null) {
      const isDisconnected =
        this.disconnectedBases.findIndex((base) => base.id === latestShift.baseId) > -1;
      return isDisconnected;
    }
    return false;
  }

  public async onSearch(): Promise<void> {
    this.submitting = true;
    this.flightNotFound = false;

    if (await this.isUserBaseDisconnected()) {
      // create new log and redirect to edit page
      const mode = this.getLogMode();
      const dispatchId = this.dispatchId.value;
      this.dialogRef.close();
      await this._router.navigate([`/flight-logs/create`], {
        queryParams: { mode, dispatchId },
      });
    } else {
      const dispatchId = this.dispatchId.value as string;
      const results$ = combineLatest([
        this._flights
          .getById(dispatchId)
          // converting to promise so errors can be caught without breaking the stream
          .toPromise()
          .catch((err) => (err.status === 404 ? null : err)),
        this._flightLogs
          .getManyFindMyFlight(dispatchId, this._user.companyId)
          .pipe(map((r) => r.results)),
      ]).pipe(
        map(([flightSummary, flightLogs]) => {
          return {
            flightSummary: flightSummary == null ? 'Unknown' : flightSummary,
            data: flightLogs.map((flightLog) => {
              return {
                logID: flightLog.id,
                dateOfService:
                  flightLog.flightTime != null
                    ? formatMomentDate(flightLog.flightTime, 'MM/DD/YYYY HH:mm')
                    : null,
                aircraftTailNumber: flightLog.tailNumber == null ? 'Unknown' : flightLog.tailNumber,
                status: humanize(flightLog.status),
                logExists: flightLog.id != null ? true : false,
              };
            }),
          };
        }),
      );
      this.searchResults = await firstValueOrNull(results$);

      if (this.searchResults == null || this.searchResults.data.length < 1) {
        // display not found message
        this.flightNotFound = true;
      } else if (this.searchResults.data.length > 0) {
        // flightlogs found, so auto size the form to fit the grid
        this.dialogRef.updateSize();
      }
    }
    this.submitting = false;
  }

  public async onAddAnotherLog() {
    this.submitting = true;
    // create an extra log and redirect to edit page
    const flightLogId = await this.createFlightLogFromFlightSummary();
    await this.goEditFlightLog(flightLogId);
    this.submitting = false;
  }

  public async onViewLog(logId: string) {
    this.submitting = true;
    this.dialogRef.close();
    this._router.navigate([`/flight-logs/${logId}`]);
    this.submitting = false;
  }

  public async onAddNewLog() {
    this.submitting = true;
    // create new log and redirect to edit page
    const flightLogId = await this.createFlightLogFromFlightSummary();
    await this.goEditFlightLog(flightLogId);
    this.submitting = false;
  }

  public async onAddNewLogNoCADInfo() {
    this.submitting = true;
    const result = await this._dialogService
      .openConfirm({
        title: 'Add Flight Log?',
        message: 'There is no record of this flight, are you sure you want to create a Flight Log?',
        acceptButton: 'Yes',
        cancelButton: 'No',
      })
      .afterClosed()
      .toPromise();

    if (result === true) {
      const mode = this.getLogMode();
      const dispatchId = this.dispatchId.value;
      this.dialogRef.close();
      await this._router.navigate([`/flight-logs/create`], {
        queryParams: { mode, dispatchId },
      });
    } else {
      this.flightNotFound = false;
    }

    this.submitting = false;
  }

  public onCancel() {
    if (this.hasFlightLogData) {
      // reset form back to the create log state
      this.searchResults = null;
      this.setDefaultDialogSize();
    } else {
      this.dialogRef.close();
    }
  }

  private setDefaultDialogSize() {
    this.dialogRef.updateSize('400px');
  }

  private async createFlightLogFromFlightSummary(): Promise<string> {
    const flightLogId = uuid();
    const mode = this.getLogMode();
    await this._flightLogs
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .createFromFlightSummary(flightLogId, this.searchResults!.flightSummary, mode)
      .toPromise();
    await this._flightLogs.begin(flightLogId, mode).toPromise();
    return flightLogId;
  }

  private getLogMode(): LogMode {
    return this._user.flightLogFeatures.perFlightLogging
      ? LogMode.EntryPerFlight
      : this._user.flightLogFeatures.perLegLogging
      ? LogMode.EntryPerLeg
      : LogMode.Simple;
  }

  private async goEditFlightLog(flightLogId: string) {
    this.dialogRef.close();
    await this._router.navigate([`/flight-logs/${flightLogId}/edit`]);
  }
}
